import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import Title from "@/components/Title";
import { Module } from "@/constants";
import { PayrollCreateButton } from "@/modules/payrolls/components/PayrollCreateButton";
import { usePayrollDetails } from "@/modules/payrolls/hooks/usePayrollDetails";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { payrollDepartmentBasedInspectionPreview } from "@/schemas/payrollDepartmentBasedInspection";
import { payrollRoleBasedInspectionPreview } from "@/schemas/payrollRoleBasedInspection";
import { FetchListRequest } from "@/services/api";
import { PayrollsService } from "@/services/payrolls";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { usePayrollBreadcrumbs } from "../hooks/usePayrollBreadcrumbs";
import { usePayrollPreviewColumnOverrides } from "../hooks/usePayrollPreviewColumnOverrides";
import { initialColumnVisibility } from "../initialColumnVisibility";
import { ReadPayroll } from "@/types/payroll";
import { PayrollInspectionPreview } from "../types";
import { PayrollRecordView } from "./PayrollRecordView";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { PayrollActionLinks } from "./PayrollActionLinks";
import { PayrollCorrectionButton } from "./PayrollCorrectionButton";

const payrollRoleBasedInspectionPreviewDisplaySchema =
  payrollRoleBasedInspectionPreview
    .unwrap()
    .omit({
      contractor_card_mimetype: true,
      contractor_card_filename: true,
    })
    .readonly();

interface PayrollPreviewTableProps {
  payrollData: ReadPayroll;
  payrollId: string;
}

export const PayrollPreviewTable = memo(
  ({ payrollData, payrollId }: PayrollPreviewTableProps) => {
    const { t } = useTranslation();

    const isRoleBased = isProjectRoleDependent(payrollData);

    const permissions = usePayrollPermissions();

    const fetchListFn = useCallback(
      (params: FetchListRequest<PayrollInspectionPreview>) =>
        PayrollsService.getPreviewList(
          payrollId,
          payrollData.project_depends_on,
          params,
        ),
      [payrollId, payrollData.project_depends_on],
    );

    const nameSuffix = isRoleBased
      ? "preview-role-based"
      : "preview-department-based";

    const { name, options, query, queryValue } =
      usePayrollDetails<PayrollInspectionPreview>({
        fetchListFn,
        nameSuffix,
      });

    const tableName = `${name}-${nameSuffix}`;

    const breadcrumbs = usePayrollBreadcrumbs([
      {
        label: t("payrolls.views.preview.title"),
      },
    ]);

    const schema = isRoleBased
      ? payrollRoleBasedInspectionPreviewDisplaySchema
      : payrollDepartmentBasedInspectionPreview;

    const columnOverrides = usePayrollPreviewColumnOverrides(isRoleBased);

    return (
      <QueryTable
        schema={schema}
        name={tableName}
        translationKey={name}
        query={query}
        options={options}
        columnOverrides={columnOverrides}
        tableFeatures={{
          initialColumnVisibility,
          exportSettings: {
            moduleName: Module.Payrolls,
            queryParams: {
              payroll_stage: isRoleBased
                ? "preview-role-based"
                : "preview-department-based",
              payroll_id: payrollId,
            },
          },
        }}
      >
        <QueryTable.Header
          breadcrumbLabel={`${name}.title.multiple`}
          breadcrumbs={breadcrumbs}
          actions={[
            permissions.canCorrect && <PayrollCorrectionButton />,
            <PayrollCreateButton
              projectDependsOn={payrollData.project_depends_on}
            />,
          ]}
        />
        <Title>{t("payrolls.views.preview.title")}</Title>
        <PayrollRecordView
          className="mb-4"
          actions={[
            <PayrollActionLinks
              aggregateOptions={{ isGrayed: true, isDisabled: true }}
              inspectOptions={{ isGrayed: true, isDisabled: true }}
              splitOptions={{ isGrayed: true, isDisabled: true }}
            />,
          ]}
        />
        <QueryTable.Menubar>
          <QueryTable.Menubar.Tools />
          <QueryTable.Menubar.Columns />
          <QueryTable.Menubar.Settings />
        </QueryTable.Menubar>
        <DataTableQueryContextProvider value={queryValue}>
          <QueryTable.Content>
            <QueryTable.Status />
          </QueryTable.Content>
        </DataTableQueryContextProvider>
      </QueryTable>
    );
  },
);
